import { useState } from "react";
import { api } from "../lib/config";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { CreditCard, Repeat, XCircle, Loader2 } from "lucide-react";

/** iter260 — Per-property AutoPay switch (customer or admin). */
export const PropertyAutopayToggle = ({ property, userId, mode = "customer", onChanged }) => {
  const [busy, setBusy] = useState(false);
  const on = !!property.autopay_effective;
  const toggle = async (next) => {
    setBusy(true);
    try {
      const url = mode === "admin"
        ? `/admin/customers/${userId}/properties/${property.property_id}/autopay`
        : `/me/properties/${property.property_id}/autopay`;
      const { data } = await api.put(url, { enabled: next });
      toast.success(`AutoPay ${next ? "on" : "off"} for ${property.label || "this property"}` +
        (data?.future_visits_updated ? ` · ${data.future_visits_updated} upcoming visit(s) updated` : ""));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update AutoPay");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/60 px-3 py-2"
         data-testid={`property-autopay-${property.property_id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <CreditCard className={`w-3.5 h-3.5 shrink-0 ${on ? "text-emerald-700" : "text-gray-400"}`} />
        <div className="min-w-0">
          <div className="text-[11px] font-extrabold text-gray-900" data-testid={`property-autopay-state-${property.property_id}`}>
            AutoPay {on ? "ON" : "OFF"}
          </div>
          <div className="text-[10px] text-gray-500 truncate">
            {!property.has_saved_card
              ? "Add a card on file to enable"
              : property.autopay_source === "property" ? "Set for this property" : "Using account setting"}
          </div>
        </div>
      </div>
      {busy ? <Loader2 className="w-4 h-4 animate-spin text-emerald-700" /> : (
        <Switch checked={on} onCheckedChange={toggle}
                disabled={!property.has_saved_card && !on}
                data-testid={`property-autopay-switch-${property.property_id}`} />
      )}
    </div>
  );
};

/** iter260 — Recurring Extra Work attached to a property's schedule. */
export const RecurringExtrasList = ({ schedule, userId, mode = "customer", onChanged, propertyId }) => {
  const [busy, setBusy] = useState(null);
  const extras = (schedule?.recurring_extras || []).filter((x) => x.status === "active");
  if (!extras.length) return null;
  const stop = async (x) => {
    if (!window.confirm(`Stop recurring "${x.service_name}"? It will be removed from upcoming unpaid visits.`)) return;
    setBusy(x.recurring_extra_id);
    try {
      await api.post(`/admin/customers/${userId}/recurring-extras/${x.recurring_extra_id}/stop`);
      toast.success(`${x.service_name} stopped`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop extra work");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-2 rounded-xl border border-emerald-100 bg-emerald-50/50 px-3 py-2"
         data-testid={`recurring-extras-${propertyId}`}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-extrabold text-emerald-800 flex items-center gap-1">
        <Repeat className="w-2.5 h-2.5" /> Recurring extra work
      </div>
      {extras.map((x) => (
        <div key={x.recurring_extra_id} className="mt-1 flex items-center justify-between gap-2 text-[11px]"
             data-testid={`recurring-extra-${x.recurring_extra_id}`}>
          <span className="font-bold text-gray-800 truncate">
            {x.service_name} · {x.frequency_label}
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="font-extrabold tabular-nums text-emerald-800">${Number(x.price || 0).toFixed(2)}</span>
            {mode === "admin" && (
              <button type="button" onClick={() => stop(x)} disabled={busy === x.recurring_extra_id}
                      className="p-0.5 rounded hover:bg-red-50" title="Stop recurring"
                      data-testid={`recurring-extra-stop-${x.recurring_extra_id}`}>
                {busy === x.recurring_extra_id
                  ? <Loader2 className="w-3 h-3 animate-spin text-red-600" />
                  : <XCircle className="w-3.5 h-3.5 text-red-600" />}
              </button>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};
